import { formatClockTime } from '@/shared/lib/time-format';
import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import { useCalendarSettingsList } from '@/settings/services/use-calendar-settings-list';
import type { SlotResponse } from '@/types/scheduling-api';

/**
 * SlotSelectionPopover — Flutter parity with `slot_selection_popup_card.dart`
 * (546 LOC). Triggered by clicking the slot-count number on a provider
 * card (Flutter `calendar_tab.dart:4320-4358` InkWell). Anchored to
 * the click target via Radix Popover.
 *
 * Behavior:
 *   - Header date label + chevron → opens shadcn Calendar to jump days.
 *   - Date strip: N days starting today, where N comes from
 *     `calendarSettings[0].showAvailableSlotsToPracticeDaysAhead`
 *     (defaults 365 — Flutter `:248-251`).
 *   - Slots grouped by hour bucket (Flutter `:114-125`):
 *       MORNING   — start.hour in 0..11
 *       AFTERNOON — start.hour in 12..16
 *       EVENING   — start.hour in 17..22
 *   - Footer: "Showing slots for the {profile.profileName} profile."
 *   - Empty / no-settings states render verbatim Flutter copy.
 *
 * Data flow: both hooks (`useCalendarSettingsList` +
 * `useAvailableSlots`) already exist in the codebase. The popover
 * just wires them.
 */

interface SlotSelectionPopoverProps {
  /** The clickable trigger element (typically the "{N} slots today" span). */
  children: React.ReactNode;
  practitionerId: string;
  practiceId: string;
  organizationId: string;
  /** Preferred appointment profile id — Flutter picks
   *  `doc.appointmentProfile.first(isPreferred)` at `:4324`. */
  profileId: string;
  /** Profile name for the footer label. */
  profileName: string;
  /** Fires when the user clicks a slot. Popover auto-closes after. */
  onSelectSlot?: (slot: SlotResponse) => void;
  /** Slot length in minutes — used by the slot fetcher. Default 100
   *  to match Flutter's `count: 100` (`:101`). */
  count?: number;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatHeaderDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Slot label "09:35" / "12:15" — or "9:35a" / "12:15p" under the 12-hour preference (Flutter
 *  `scTimeFormatSingleLetter`). No-Z ISO from the backend is treated as wall-clock local time. */
function formatSlotChip(iso: string): string {
  return formatClockTime(new Date(iso));
}

interface SlotGroups {
  morning: SlotResponse[];
  afternoon: SlotResponse[];
  evening: SlotResponse[];
}

function bucketSlots(slots: SlotResponse[]): SlotGroups {
  const morning: SlotResponse[] = [];
  const afternoon: SlotResponse[] = [];
  const evening: SlotResponse[] = [];
  for (const s of slots) {
    if (!s.start) continue;
    const h = new Date(s.start).getHours();
    if (h >= 0 && h < 12) morning.push(s);
    else if (h >= 12 && h < 17) afternoon.push(s);
    else if (h >= 17 && h < 23) evening.push(s);
  }
  return { morning, afternoon, evening };
}

export function SlotSelectionPopover({
  children,
  practitionerId,
  practiceId,
  organizationId,
  profileId,
  profileName,
  onSelectSlot,
  count = 100,
}: SlotSelectionPopoverProps) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Calendar settings — drives the date strip range. Only enabled
  // once the popover opens to avoid unnecessary requests for every
  // provider card on the strip.
  const settingsQuery = useCalendarSettingsList(
    open ? practitionerId : null,
    open ? organizationId : null,
  );
  const settings = settingsQuery.data ?? [];
  // Find the matching practice (Flutter `:248` takes `.elementAt(0)`
  // — first item — but the response contains all practices for this
  // doctor. Prefer the matching practiceId; fall back to first.).
  const calendarSettings = settings.find((s) => s.practiceId === practiceId) ?? settings[0] ?? null;
  const daysAhead = Math.min(calendarSettings?.showAvailableSlotsToPracticeDaysAhead ?? 365, 365);

  // Slot list for the selected day.
  const slotsQuery = useAvailableSlots(
    open
      ? {
          practiceId,
          practitionerId,
          appointmentProfileId: profileId,
          start: isoDay(selectedDate),
          end: isoDay(selectedDate),
          count,
        }
      : null,
  );

  const groups = useMemo(() => bucketSlots(slotsQuery.data ?? []), [slotsQuery.data]);

  // Date-strip range. Floor at today (negative offsets aren't useful here
  // since the API rejects past slot queries anyway).
  const today = startOfDay(new Date());
  const lastDate = startOfDay(new Date());
  lastDate.setDate(lastDate.getDate() + daysAhead - 1);

  const dateCells = useMemo(() => {
    const cells: { date: Date; iso: string; month: string; day: number; weekday: string }[] = [];
    // Build a window of 60 cards for visual density; the calendar
    // popover handles longer reaches. Matches the visual weight of
    // the block-availability strip.
    const stripDays = Math.min(daysAhead, 60);
    for (let i = 0; i < stripDays; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      cells.push({
        date: d,
        iso: isoDay(d),
        month: MONTH_ABBR[d.getMonth()],
        day: d.getDate(),
        weekday: WEEKDAY_ABBR[d.getDay()],
      });
    }
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead, today.getTime()]);

  const selectedIso = isoDay(selectedDate);
  const isLoading = settingsQuery.isLoading || slotsQuery.isLoading;
  const noSettings = settings.length > 0 && !calendarSettings;
  const slotsBlocked = (calendarSettings?.showAvailableSlotsToPracticeDaysAhead ?? 0) === 0;

  const handleSlotClick = (slot: SlotResponse) => {
    onSelectSlot?.(slot);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        // Base UI's `render` prop wraps the trigger button. We let
        // the consumer pass any inline element as the trigger via
        // children — same pattern as the appointment popup elsewhere.
        className="inline cursor-pointer p-0 m-0 border-0 bg-transparent text-inherit font-inherit"
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[344px] max-h-[504px] p-0 overflow-hidden flex flex-col"
      >
        {/* Header date — clickable opens shadcn Calendar to jump days. */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-center flex-shrink-0">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger className="flex items-center gap-2 text-sm font-bold text-text-primary hover:text-brand-primary transition-colors">
              <span>{formatHeaderDate(selectedDate)}</span>
              <ChevronDown className="h-4 w-4 text-brand-primary" />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  if (d) {
                    setSelectedDate(startOfDay(d));
                    setCalendarOpen(false);
                  }
                }}
                disabled={(d) => d < today || d > lastDate}
                initialFocus
              />
              {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
              <div className="mt-2 flex items-center justify-between border-t border-[var(--surface-line)] pt-2 text-size-sm">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDate(startOfDay(new Date()));
                    setCalendarOpen(false);
                  }}
                  className="font-medium text-brand-primary hover:underline"
                >
                  Today
                </button>
                <span className="text-text-muted">{formatHeaderDate(selectedDate)}</span>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Horizontal date strip. */}
        <div className="px-4 pb-3 flex-shrink-0">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" role="listbox" aria-label="Select date">
            {dateCells.map((cell) => {
              const isSelected = cell.iso === selectedIso;
              return (
                <button
                  key={cell.iso}
                  data-iso={cell.iso}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => setSelectedDate(cell.date)}
                  className={`flex-shrink-0 w-[64px] h-[80px] flex flex-col items-center justify-center rounded-card border transition-colors ${
                    isSelected
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-[var(--surface-read)] text-text-primary border-[var(--surface-line)] hover:bg-grey-100'
                  }`}
                >
                  <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>
                    {cell.month}
                  </span>
                  <span className="[font:var(--type-card-title)]">{cell.day}</span>
                  <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>
                    {cell.weekday}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Slots — scrollable body. */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-4 pb-3">
          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-brand-primary border-t-transparent animate-spin" />
            </div>
          ) : noSettings || slotsBlocked ? (
            // Flutter `:302`: "Booking not allowed, Please review settings"
            <p className="[font:var(--type-body)] text-[var(--ink-meta)] text-center py-10">
              Booking not allowed, Please review settings
            </p>
          ) : (slotsQuery.data ?? []).length === 0 ? (
            // Flutter `:303`: "No available slots"
            <p className="[font:var(--type-body)] text-[var(--ink-meta)] text-center py-10">No available slots</p>
          ) : (
            <div className="space-y-4">
              {groups.morning.length > 0 && (
                <SlotGroup label="Morning" slots={groups.morning} onSelect={handleSlotClick} />
              )}
              {groups.afternoon.length > 0 && (
                <SlotGroup label="Afternoon" slots={groups.afternoon} onSelect={handleSlotClick} />
              )}
              {groups.evening.length > 0 && (
                <SlotGroup label="Evening" slots={groups.evening} onSelect={handleSlotClick} />
              )}
            </div>
          )}
        </div>

        {/* Footer — Flutter `:514-526` (profile name caption). */}
        {!isLoading && (slotsQuery.data ?? []).length > 0 && (
          <div className="border-t border-[var(--surface-line)] px-4 py-3 flex-shrink-0">
            <p className="text-xs text-text-muted text-center">
              Showing slots for the <span className="font-semibold">{profileName}</span> profile.
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface SlotGroupProps {
  label: string;
  slots: SlotResponse[];
  onSelect: (slot: SlotResponse) => void;
}

function SlotGroup({ label, slots, onSelect }: SlotGroupProps) {
  return (
    <div>
      <p className="text-xs font-medium text-brand-primary mb-2">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => (
          <button
            key={`${slot.start}-${slot.id}`}
            type="button"
            onClick={() => onSelect(slot)}
            className="px-3 h-8 rounded-full border border-[var(--surface-line)] bg-[var(--surface-read)] text-sm font-medium text-text-primary hover:border-brand-primary hover:bg-[var(--accent-active-bg)] transition-colors"
          >
            {formatSlotChip(slot.start)}
          </button>
        ))}
      </div>
    </div>
  );
}
